import React, {useEffect} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {SwitchButton} from '@lightricks/react-design-system';
import useFontManager from '@contentlab/hooks/use-font-manager';
import {FontManager} from '@contentlab/services/fonts';
import {fontsStoreActions} from '@contentlab/stores/fontStore';
import {
  FontVariants,
  TextCapitalization,
  CaptionsStylePreset,
} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import useDebouncedCallback from '@/hooks/use-debounce/useDebouncedCallback';
import {
  SectionLabel,
  StyledSlider,
  ColorPicker,
  LabeledSlider,
  DividerLine,
  StyledToggleContainer,
  FALLBACK_FONT_FAMILIES,
  FONT_SIZE_OPTIONS,
  designSystemToken,
  Icon,
  Button,
  Alignment,
} from './SharedConstants';
import StyleEditorDropdown from './StyleEditorDropdown';

// Helper function to get display name for font variants
const getVariantDisplayName = (variant: FontVariants): string => {
  switch (variant) {
    case FontVariants.REGULAR:
      return 'Regular';
    case FontVariants.BOLD:
      return 'Bold';
    case FontVariants.ITALIC:
      return 'Italic';
    default:
      // Fallback for any future variants not explicitly handled
      return String(variant).charAt(0).toUpperCase() + String(variant).slice(1);
  }
};

// Helper function to format line height percentage for display
const formatLineHeightPercentage = (lineHeight: number | undefined): string => {
  const value = lineHeight ?? DEFAULT_LINE_HEIGHT_MULTIPLIER;
  return `${Math.round(value * 100)}%`;
};

const AlignmentGroup = styled(Box)(() => ({
  display: 'flex',
  gap: '8px',
  width: '100%',
}));

const AlignmentButton = styled(Button)<{selected?: boolean}>(({selected}) => ({
  'flex': 1,
  'minWidth': '40px',
  'height': '40px',
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  'border': selected
    ? `1px solid ${designSystemToken('semantic.bg.neutral')}`
    : `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  'borderRadius': '8px',
  'padding': '16px',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.primary'),
    opacity: 0.8,
  },
}));

interface TypographySectionProps {
  updateTypography: (
    updates: Partial<CaptionsStylePreset['captionsStyle']['typography']>
  ) => void;
  editedStyle: CaptionsStylePreset;
}

// Default values
const DEFAULT_FONT_PLACEHOLDER = 'Font Name';
const DEFAULT_SIZE_PLACEHOLDER = '16';
const DEFAULT_VARIANT_PLACEHOLDER = 'Regular';
const DEFAULT_LINE_HEIGHT_MULTIPLIER = 1.0;
const DEFAULT_KERNING = 0;
const MIN_LINE_HEIGHT = 0.5;
const MAX_LINE_HEIGHT = 5.0;
const LINE_HEIGHT_STEP = 0.05;
const MIN_KERNING = -5;
const MAX_KERNING = 200;
const KERNING_STEP = 1;
const ALIGNMENT_BUTTON_PROPS = {
  appearance: 'neutral' as const,
  mode: 'tinted' as const,
  size: 'small' as const,
};

// Utility methods
function FontFamilySelector({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  useFontManager();
  const allFonts = fontsStoreActions.getAllFonts();

  const loadFontDebounced = useDebouncedCallback(
    async (fontId: string, variant: FontVariants) => {
      try {
        await FontManager.loadFont(fontId, variant);
      } catch (error) {
        console.warn(`Failed to load font ${fontId}/${variant}:`, error);
      }
    },
    50
  );

  const currentFontId =
    editedStyle.captionsStyle.typography.fontSettings.selectedFont?.fontId ||
    FALLBACK_FONT_FAMILIES[0].id;

  // Use fonts from metadata, fallback to hardcoded list if none loaded
  const baseFonts = allFonts.length > 0 ? allFonts : FALLBACK_FONT_FAMILIES;
  const fontOptions = baseFonts.map((font) => ({
    id: font.id,
    name: font.name,
    overridingFontFamily: font.name,
  }));

  const handleFontChange = (fontId: string) => {
    const currentVariant =
      editedStyle.captionsStyle.typography.fontSettings.selectedFont?.variant ||
      'regular';

    // Find the new font to check available variants
    const newFont = allFonts.find((font) => font.id === fontId);
    const DEFAULT_VARIANTS = [FontVariants.REGULAR];
    const availableVariants =
      newFont?.downloadUrls && Object.keys(newFont.downloadUrls).length > 0
        ? (Object.keys(newFont.downloadUrls) as FontVariants[])
        : DEFAULT_VARIANTS;

    // Try to preserve the current variant if available, otherwise use the first available variant
    const variantToUse = availableVariants.includes(
      currentVariant as FontVariants
    )
      ? currentVariant
      : availableVariants[0];

    if (variantToUse !== currentVariant) {
      console.log(
        `Font variant fallback: ${currentVariant} not available in ${fontId}, using ${variantToUse}`
      );
    }

    // Update the typography immediately
    updateTypography({
      fontSettings: {
        ...editedStyle.captionsStyle.typography.fontSettings,
        selectedFont: {
          fontId,
          variant: variantToUse,
        },
      },
    });

    // Load the font in the background with debounce
    loadFontDebounced(fontId, variantToUse);
  };

  return (
    <StyleEditorDropdown
      value={currentFontId}
      options={fontOptions}
      onChange={handleFontChange}
      placeholder={DEFAULT_FONT_PLACEHOLDER}
    />
  );
}

function FontSizeAndVariantSelectors({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  useFontManager();
  const allFonts = fontsStoreActions.getAllFonts();

  const loadFontDebounced = useDebouncedCallback(
    async (fontId: string, variant: FontVariants) => {
      try {
        await FontManager.loadFont(fontId, variant);
      } catch (error) {
        console.warn(`Failed to load font ${fontId}/${variant}:`, error);
      }
    },
    50
  );

  const currentFontId =
    editedStyle.captionsStyle.typography.fontSettings.selectedFont?.fontId ||
    FALLBACK_FONT_FAMILIES[0].id;
  const currentVariant =
    editedStyle.captionsStyle.typography.fontSettings.selectedFont?.variant ||
    'regular';

  // Get available variants for the current font
  const currentFont = allFonts.find((font) => font.id === currentFontId);

  // Better fallback: if font not found or no variants, show all standard variants
  const DEFAULT_VARIANTS = [FontVariants.REGULAR];
  const availableVariants =
    currentFont?.downloadUrls &&
    Object.keys(currentFont.downloadUrls).length > 0
      ? (Object.keys(currentFont.downloadUrls) as FontVariants[])
      : DEFAULT_VARIANTS;

  // Ensure current variant is valid for the current font, fallback if needed
  const validCurrentVariant = availableVariants.includes(
    currentVariant as FontVariants
  )
    ? currentVariant
    : availableVariants[0];

  // Auto-correct invalid variant for current font
  useEffect(() => {
    if (validCurrentVariant !== currentVariant) {
      console.log(
        `Auto-correcting invalid variant: ${currentVariant} → ${validCurrentVariant} for font ${currentFontId}`
      );
      updateTypography({
        fontSettings: {
          ...editedStyle.captionsStyle.typography.fontSettings,
          selectedFont: {
            fontId: currentFontId,
            variant: validCurrentVariant,
          },
        },
      });
    }
  }, [
    currentFontId,
    validCurrentVariant,
    currentVariant,
    editedStyle.captionsStyle.typography.fontSettings,
  ]);

  const handleVariantChange = (variant: FontVariants) => {
    // Update the typography immediately
    updateTypography({
      fontSettings: {
        ...editedStyle.captionsStyle.typography.fontSettings,
        selectedFont: {
          fontId: currentFontId,
          variant,
        },
      },
    });

    // Load the specific variant in the background with debounce
    loadFontDebounced(currentFontId, variant);
  };

  const fontSizeOptions = FONT_SIZE_OPTIONS.map((size) => ({
    id: size.value.toString(),
    name: size.label,
  }));

  const handleFontSizeChange = (value: string) => {
    updateTypography({
      fontSettings: {
        ...editedStyle.captionsStyle.typography.fontSettings,
        size: Number(value),
      },
    });
  };

  return (
    <Stack direction="row" gap="8px">
      <Box width="100%">
        <StyleEditorDropdown
          value={editedStyle.captionsStyle.typography.fontSettings.size.toString()}
          options={fontSizeOptions}
          onChange={handleFontSizeChange}
          placeholder={DEFAULT_SIZE_PLACEHOLDER}
        />
      </Box>

      <Box width="100%">
        <StyleEditorDropdown
          value={validCurrentVariant}
          options={availableVariants.map((variant) => ({
            id: variant,
            name: getVariantDisplayName(variant),
          }))}
          onChange={(value) => handleVariantChange(value as FontVariants)}
          placeholder={DEFAULT_VARIANT_PLACEHOLDER}
        />
      </Box>
    </Stack>
  );
}

function FontColorPicker({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateTypography({
      textColor: {
        ...editedStyle.captionsStyle.typography.textColor,
        hex: e.target.value,
      },
    });
  };

  return (
    <ColorPicker
      label="Font Color"
      value={editedStyle.captionsStyle.typography.textColor.hex}
      onChange={handleColorChange}
    />
  );
}

function FontSettingsSection({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  return (
    <Box>
      <SectionLabel size="md">{translate('Font')}</SectionLabel>
      <Stack gap="24px">
        <FontFamilySelector
          updateTypography={updateTypography}
          editedStyle={editedStyle}
        />
        <FontSizeAndVariantSelectors
          updateTypography={updateTypography}
          editedStyle={editedStyle}
        />
        <FontColorPicker
          updateTypography={updateTypography}
          editedStyle={editedStyle}
        />
      </Stack>
    </Box>
  );
}

function AlignmentSection({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  return (
    <Box>
      <SectionLabel size="md">{translate('Align')}</SectionLabel>
      <AlignmentGroup>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textAlignment ===
            Alignment.LEFT
          }
          onClick={() => updateTypography({textAlignment: Alignment.LEFT})}
        >
          <Icon
            name="Navigation-Align-Left"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textAlignment ===
            Alignment.CENTER
          }
          onClick={() => updateTypography({textAlignment: Alignment.CENTER})}
        >
          <Icon
            name="Navigation-Align-Center"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textAlignment ===
            Alignment.RIGHT
          }
          onClick={() => updateTypography({textAlignment: Alignment.RIGHT})}
        >
          <Icon
            name="Navigation-Align-Right"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
      </AlignmentGroup>
    </Box>
  );
}

function CapitalizationSection({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  return (
    <Box>
      <SectionLabel size="md">{translate('Capitalization')}</SectionLabel>
      <AlignmentGroup>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textCapitalization ===
            TextCapitalization.UPPERCASE
          }
          onClick={() =>
            updateTypography({
              textCapitalization: TextCapitalization.UPPERCASE,
            })
          }
        >
          <Icon
            name="StateBar-Capitalization-Titlecase"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textCapitalization ===
            TextCapitalization.LOWERCASE
          }
          onClick={() =>
            updateTypography({
              textCapitalization: TextCapitalization.LOWERCASE,
            })
          }
        >
          <Icon
            name="StateBar-Capitalization-Lowercase"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
        <AlignmentButton
          {...ALIGNMENT_BUTTON_PROPS}
          selected={
            editedStyle.captionsStyle.typography.textCapitalization ===
            TextCapitalization.REGULAR
          }
          onClick={() =>
            updateTypography({textCapitalization: TextCapitalization.REGULAR})
          }
        >
          <Icon
            name="StateBar-Capitalization-Uppercase"
            size="medium"
            appearance="neutral"
          />
        </AlignmentButton>
      </AlignmentGroup>
    </Box>
  );
}

function SpacingSection({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  const handleSpacingToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      updateTypography({
        spacing: {
          lineHeight: DEFAULT_LINE_HEIGHT_MULTIPLIER,
          kerning: DEFAULT_KERNING,
        },
      });
    } else {
      updateTypography({
        spacing: null,
      });
    }
  };

  return (
    <>
      <StyledToggleContainer>
        <SectionLabel size="md" sx={{mb: 0}}>
          {translate('Spacing')}
        </SectionLabel>
        <SwitchButton
          checkedTrackColor={designSystemToken('semantic.fg.primary')}
          checked={editedStyle.captionsStyle.typography.spacing !== null}
          onChange={handleSpacingToggle}
        />
      </StyledToggleContainer>
      {editedStyle.captionsStyle.typography.spacing !== null && (
        <Box mt="16px">
          <LabeledSlider
            label={translate('Line Height')}
            value={
              editedStyle.captionsStyle.typography.spacing?.lineHeight ??
              DEFAULT_LINE_HEIGHT_MULTIPLIER
            }
            displayValue={formatLineHeightPercentage(
              editedStyle.captionsStyle.typography.spacing?.lineHeight
            )}
            min={MIN_LINE_HEIGHT}
            max={MAX_LINE_HEIGHT}
            step={LINE_HEIGHT_STEP}
            onChange={(value) => {
              updateTypography({
                spacing: {
                  ...editedStyle.captionsStyle.typography.spacing!,
                  lineHeight: value,
                },
              });
            }}
            iconName="Features-Spacing"
            iconColor={designSystemToken('semantic.fg.secondary')}
            sx={{mb: '16px'}}
          />

          <LabeledSlider
            label={translate('Kerning')}
            value={
              editedStyle.captionsStyle.typography.spacing?.kerning ??
              DEFAULT_KERNING
            }
            displayValue={`${
              editedStyle.captionsStyle.typography.spacing?.kerning ??
              DEFAULT_KERNING
            }px`}
            min={MIN_KERNING}
            max={MAX_KERNING}
            step={KERNING_STEP}
            onChange={(value) => {
              updateTypography({
                spacing: {
                  ...editedStyle.captionsStyle.typography.spacing!,
                  kerning: value,
                },
              });
            }}
            iconName="Features-Kerning"
            iconColor={designSystemToken('semantic.fg.secondary')}
          />
        </Box>
      )}
    </>
  );
}

function TypographySection({
  updateTypography,
  editedStyle,
}: TypographySectionProps) {
  return (
    <Stack gap="16px">
      <FontSettingsSection
        updateTypography={updateTypography}
        editedStyle={editedStyle}
      />
      <DividerLine />
      <AlignmentSection
        updateTypography={updateTypography}
        editedStyle={editedStyle}
      />
      <DividerLine />
      <CapitalizationSection
        updateTypography={updateTypography}
        editedStyle={editedStyle}
      />
      <DividerLine />
      <SpacingSection
        updateTypography={updateTypography}
        editedStyle={editedStyle}
      />
    </Stack>
  );
}

export default TypographySection;
